/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The front door to the four ways of getting a recipe in.
 *
 * ── WHAT THIS REPLACED ──────────────────────────────────────────────────────
 * Five buttons in a row on the Recipes header: Write one · Scan a page ·
 * Import a URL · Select to share · Generate. Four ghost, one primary — and the
 * primary was "Generate", the AI path, which is the least predictable option to
 * hand a first-time user as the recommended one. Four routes to the same
 * destination, all shouting at once, plus an unrelated bulk-share action
 * wedged in among them.
 *
 * ── WHAT THIS IS NOT ────────────────────────────────────────────────────────
 * It is NOT a reduction in capability, and it is important that it stays that
 * way. Every route below opens the full-power screen it always did:
 *
 *   • Scan  → /recipes/scan, with its multi-image upload (up to 8 for a
 *             spread), the async OCR job with polling backoff, the raw
 *             page text kept visible in a disclosure, and per-field
 *             low-confidence warnings.
 *   • Link  → /recipes/import, which still distinguishes a clean schema.org
 *             read from an AI interpretation and says which happened.
 *   • Write → /recipes/new, the full editor.
 *   • AI    → /recipes/generate, with every control it ever had.
 *
 * This is progressive disclosure — one decision, then the full tool — not a
 * simplification that costs the advanced paths anything. The ordering is the
 * only opinion expressed: pasting a link is the lowest-effort route with the
 * highest success rate, so it goes first and carries the recommendation.
 */

import { useNavigate } from "react-router-dom";

import { Modal } from "../../components/ui.tsx";
import { Icon, type IconName } from "../../components/Icon.tsx";

interface Route {
  to: string;
  icon: IconName;
  title: string;
  body: string;
  /** The one we suggest. Exactly one route may set this. */
  suggested?: boolean;
}

const ROUTES: Route[] = [
  {
    to: "/recipes/import",
    icon: "link",
    title: "Paste a link",
    body: "From any recipe site. Most publish structured data we can read exactly, "
      + "with no AI involved — and we'll tell you which happened.",
    suggested: true,
  },
  {
    to: "/recipes/scan",
    icon: "camera",
    title: "Photograph a page",
    body: "A cookbook, a magazine, a card in your grandmother's handwriting. "
      + "Up to 8 photos for a spread. Anything hard to read gets flagged.",
  },
  {
    to: "/recipes/generate",
    icon: "sparkles",
    title: "Have one written",
    body: "Describe roughly what you fancy. Your household's allergens are enforced "
      + "in code after the model answers, not merely requested.",
  },
  {
    to: "/recipes/new",
    icon: "pen",
    title: "Write it yourself",
    body: "The full editor — ingredients, steps, scaling behaviour, the lot.",
  },
];

export function AddRecipeSheet({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();

  return (
    <Modal title="Add a recipe" onClose={onClose}>
      <div className="space-y-2">
        {ROUTES.map((r) => (
          <button
            key={r.to}
            type="button"
            onClick={() => { onClose(); navigate(r.to); }}
            className={`flex w-full items-start gap-3 rounded-xl border p-3.5 text-left transition ${
              r.suggested
                ? "border-accent/50 bg-accent/5 hover:border-accent"
                : "border-rule-soft hover:border-accent/50 hover:bg-paper-3"}`}
          >
            <span aria-hidden className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${
              r.suggested ? "bg-accent/15 text-accent" : "bg-paper-3 text-ink-3"}`}>
              <Icon name={r.icon} className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className="font-medium text-ink">{r.title}</span>
                {r.suggested && (
                  <span className="chip border-accent/40 text-accent">easiest</span>
                )}
              </span>
              <span className="mt-0.5 block text-sm leading-snug text-ink-3">{r.body}</span>
            </span>
            <Icon name="chevronRight" className="mt-3 h-4 w-4 shrink-0 text-ink-3" />
          </button>
        ))}
      </div>

      {/* Stated once, here, rather than repeated on all four screens. It is the
          single most reassuring fact about every one of these routes. */}
      <p className="mt-4 text-center text-xs text-ink-3">
        Whichever you choose, nothing is saved until you've looked it over.
      </p>
    </Modal>
  );
}
